import React, { useEffect, useState } from 'react';
import { X, ExternalLink, Ban } from 'lucide-react';

/**
 * Globally-mounted modal that replaces the ugly browser alert() when the
 * backend returns a Discord membership/ban error (403 not_in_discord | discord_banned).
 *
 * Pages trigger it imperatively via a CustomEvent so they don't have to manage
 * their own state. Example:
 *   window.dispatchEvent(new CustomEvent('discord:required', { detail: { kind: 'not_in_discord' } }));
 */
const DISCORD_INVITE = 'https://discord.gg/aqmopti';

const DiscordRequiredModal = () => {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('not_in_discord'); // 'not_in_discord' | 'discord_banned'

  useEffect(() => {
    const onEvt = (e) => {
      const k = e?.detail?.kind === 'discord_banned' ? 'discord_banned' : 'not_in_discord';
      setKind(k);
      setOpen(true);
    };
    window.addEventListener('discord:required', onEvt);
    return () => window.removeEventListener('discord:required', onEvt);
  }, []);

  if (!open) return null;

  const isBanned = kind === 'discord_banned';

  const handleClose = () => setOpen(false);
  const handlePrimary = () => {
    window.open(DISCORD_INVITE, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      data-testid="discord-required-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-md"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-md rounded-2xl border bg-[#0d0f1e]/95 backdrop-blur-md shadow-[0_25px_60px_-15px_rgba(0,0,0,0.85)] overflow-hidden ${
          isBanned ? 'border-red-500/40' : 'border-indigo-500/40'
        }`}
        style={{ animation: 'discordReqScaleIn 220ms cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Glow accent */}
        <div className={`pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl ${
          isBanned ? 'bg-red-500/10' : 'bg-indigo-500/10'
        }`} />

        {/* Close (×) */}
        <button
          onClick={handleClose}
          data-testid="discord-required-close"
          className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors z-10"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative p-6 md:p-8">
          {/* Icon */}
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${
            isBanned ? 'bg-red-500/10 border border-red-500/25' : 'bg-indigo-500/10 border border-indigo-500/25'
          }`}>
            {isBanned ? (
              <Ban className="w-7 h-7 text-red-400" />
            ) : (
              <svg className="w-7 h-7 text-indigo-400" viewBox="0 0 127.14 96.36" fill="currentColor">
                <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
              </svg>
            )}
          </div>

          {/* Title */}
          <h2 className="text-xl md:text-2xl font-bold text-white mb-2 tracking-tight">
            {isBanned ? 'Compte banni du Discord' : 'Rejoignez notre Discord'}
          </h2>

          {/* Description */}
          <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-6">
            {isBanned
              ? "Ton compte Discord est banni du serveur AqmOpti. Tu ne peux pas effectuer cette action. Contacte le support si tu penses qu'il s'agit d'une erreur."
              : 'Tu dois être membre de notre serveur Discord pour commander ou ouvrir un ticket. Rejoins-nous en un clic.'}
          </p>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch gap-2">
            <button
              onClick={handleClose}
              data-testid="discord-required-cancel"
              className="flex-1 px-5 py-3 rounded-xl border border-white/10 hover:border-white/20 bg-transparent text-gray-300 hover:text-white text-sm md:text-base font-medium transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handlePrimary}
              data-testid="discord-required-primary"
              className={`flex-1 px-5 py-3 rounded-xl text-white text-sm md:text-base font-semibold transition-all flex items-center justify-center gap-2 ${
                isBanned
                  ? 'bg-red-600 hover:bg-red-500 shadow-[0_0_25px_-8px_rgba(239,68,68,0.6)]'
                  : 'bg-[#5865F2] hover:bg-[#4752C4] shadow-[0_0_25px_-8px_rgba(88,101,242,0.7)]'
              }`}
            >
              {isBanned ? 'Contacter le support' : 'Discord'}
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes discordReqScaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
      `}</style>
    </div>
  );
};

/** Helper to fire the modal from anywhere */
export const showDiscordRequired = (kind = 'not_in_discord') => {
  window.dispatchEvent(new CustomEvent('discord:required', { detail: { kind } }));
};

export default DiscordRequiredModal;
